import * as Sentry from '@sentry/react';
import {
  Alert,
  Box,
  Flex,
  Button,
  Checkbox,
  Icon,
  Text,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  useDisclosure,
  CModal,
  CModalBody,
  ModalCloseButton,
  ModalContent,
  CModalFooter,
  CModalHeader,
  ModalOverlay,
  Select,
  HStack,
  VStack,
} from '@televet/kibble-ui';
import { TextInput } from '@televet/kibble-ui/build/components/TextInput';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import { useAuth } from 'shared/providers/AuthProvider';
import useClinicUser from 'shared/hooks/useClinicUser';
import {
  PhoneNumberStatus,
  ServiceProvider,
  TwilioVerificationStatus,
  useGetClinicSettingsGeneralQuery,
  useSubscribeToClinicSettingsGeneralTwilioVerificationSubscription,
  useUpdateClinicSettingsGeneralVerifyTwilioCallerIdMutation,
  useUpdateOneClinicPhoneNumberMutation,
} from 'shared/types/graphql';

type IEnableBandwidthPhoneNumberProps = {
  phoneNumber: string;
  isPrimary: boolean;
  incomingCarrier: string;
  clinicPhoneNumberId: string;
  loaSignedAt?: string;
  loaSignedByUser?: string;
  loaUrl?: string | null;
  status: PhoneNumberStatus;
  provider: ServiceProvider;
  refetchPhoneNumbers: () => void;
  isClinicDataComplete: boolean;
};

enum ModalContentSteps {
  Hosting = 'Hosting',
  PhoneVerification = 'PhoneVerification',
  LOA = 'LOA',
}

enum VerificationSteps {
  InformationCollection,
  CallVerifying,
  CallSuccessfullyVerified,
  CallVerificationUnsuccessful,
}
interface Action {
  type: string;
  value: number;
  description: string;
}

enum PhoneTreeChoices {
  Wait = 'Wait',
  Press = 'Press',
}
const twilioCodeOverloadForAlreadyVerifiedNumbers = 'VERIFIED';

const EnableBandwidthPhoneNumber = ({
  phoneNumber,
  isPrimary,
  status,
  incomingCarrier,
  clinicPhoneNumberId,
  loaSignedAt,
  loaSignedByUser,
  loaUrl,
  provider,
  refetchPhoneNumbers,
  isClinicDataComplete,
}: IEnableBandwidthPhoneNumberProps): JSX.Element => {
  const history = useHistory();
  const { isImpersonating } = useAuth();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentModalContent, setCurrentModalContent] = useState(ModalContentSteps.Hosting);
  const [phoneTreeSelections, setPhoneTreeSelections] = useState<Action[]>([]);
  const [isCallerId, setIsCallerId] = useState(false);
  const [callerIdVerificationCode, setCallerIdVerificationCode] = useState('');
  const [twilioCallSid, setTwilioCallSid] = useState<string>();
  const [verificationError, setVerificationError] = useState<string>('');
  const [currentVerificationStep, setCurrentVerificationStep] = useState<VerificationSteps>(
    VerificationSteps.InformationCollection,
  );
  const [isLOACheckedBox, setIsLOACheckedBox] = useState(false);
  const [loaValidationError, setLOAValidationError] = useState('');
  const [displayLastWarning, setDisplayLastWarning] = useState(false);

  const { clinicUser, currentClinicId } = useClinicUser();

  const resetState = (): void => {
    setCurrentModalContent(ModalContentSteps.Hosting);
    setPhoneTreeSelections([]);
    setIsCallerId(false);
    setCallerIdVerificationCode('');
    setTwilioCallSid(undefined);
    setVerificationError('');
    setCurrentVerificationStep(VerificationSteps.InformationCollection);
    setIsLOACheckedBox(false);
    setLOAValidationError('');
    setDisplayLastWarning(false);
  };

  useEffect(() => {
    resetState();
  }, [currentClinicId]);

  const handleEnablePhoneNumber = (): void => {
    onOpen();
  };

  const handleStartPorting = (): void => {
    resetState();
    onClose();
    updateOneClinicPhoneNumber({
      variables: {
        where: {
          id: clinicPhoneNumberId,
        },
        data: { status: PhoneNumberStatus.PortingInProgress },
      },
    });
  };

  const handleActivatePhoneNumber = async (): Promise<void> => {
    await updateOneClinicPhoneNumber({
      variables: {
        where: {
          id: clinicPhoneNumberId,
        },
        data: { isPrimary: true },
      },
    });
    refetchPhoneNumbers();
  };

  const addNewPhoneTreeStep = (): void => {
    setPhoneTreeSelections((currentPhoneTreeSteps) => [
      ...currentPhoneTreeSteps,
      {
        type: PhoneTreeChoices.Wait,
        value: 5,
        description: 'second(s)',
      },
    ]);
  };

  const changePhoneTreeSelectionTypeAtIndex = (value: string, index: number): void => {
    const newPhoneTreeSelections = [...phoneTreeSelections];
    newPhoneTreeSelections[index].type = value;
    newPhoneTreeSelections[index].description = value === PhoneTreeChoices.Wait ? 'second(s)' : 'key';
    setPhoneTreeSelections(newPhoneTreeSelections);
  };

  const changePhoneTreeSelectionValueAtIndex = (value: number, index: number): void => {
    const newPhoneTreeSelections = [...phoneTreeSelections];
    newPhoneTreeSelections[index].value = value;
    setPhoneTreeSelections(newPhoneTreeSelections);
  };

  const removePhoneTreeSelectionAtIndex = (index: number): void => {
    const newPhoneTreeSelections = [...phoneTreeSelections];
    newPhoneTreeSelections.splice(index, 1);
    setPhoneTreeSelections(newPhoneTreeSelections);
  };

  const [updateOneClinicPhoneNumber] = useUpdateOneClinicPhoneNumberMutation();

  const signLettersOfAuthorization = async (): Promise<void> => {
    if (userId) {
      if (isLOACheckedBox) {
        const loaSignedAt = new Date().toISOString();
        await updateOneClinicPhoneNumber({
          variables: {
            where: {
              id: clinicPhoneNumberId,
            },
            data: { loaSignedByUser: { connect: { id: userId } }, loaSignedAt },
          },
        });
        setCurrentModalContent(ModalContentSteps.Hosting);
      } else {
        setLOAValidationError('Confirmation box must be checked.');
      }
    }
  };

  const [twilioVerifyCallerId] = useUpdateClinicSettingsGeneralVerifyTwilioCallerIdMutation({
    errorPolicy: GraphQLErrorPolicies.All,
  });

  const userId = clinicUser?.id;
  const userEmail = clinicUser?.email;

  const userName = `${clinicUser?.firstName} ${clinicUser?.lastName}`;

  const { data: currentClinic, refetch: refetchCurrentClinicData } = useGetClinicSettingsGeneralQuery({
    variables: { id: currentClinicId || '' },
    skip: !currentClinicId,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
  });

  // Some 1.0 Clinics have a number verified in twilio with no record
  // in TwilioVerification table. This will check if the number is already verified in twilio
  // and refetch clinic data if it is. We depend on a twilio webhook to update the verification
  // status in the TwilioVerification table. This webhook will never fire so we need handle that case
  useEffect(() => {
    if (callerIdVerificationCode === twilioCodeOverloadForAlreadyVerifiedNumbers) {
      refetchCurrentClinicData();
      setCurrentVerificationStep(VerificationSteps.CallSuccessfullyVerified);
    }
  }, [callerIdVerificationCode, refetchCurrentClinicData]);

  const isNumberVerified = useMemo(() => {
    for (const twilioVerification of currentClinic?.findUniqueClinic?.twilioVerifications || []) {
      if (
        twilioVerification.status === TwilioVerificationStatus.Success &&
        twilioVerification.phoneNumber === phoneNumber.replace(/-/g, '')
      ) {
        return true;
      }
    }
    return false;
  }, [currentClinic?.findUniqueClinic?.twilioVerifications, phoneNumber]);

  const isLOASigned = useMemo(() => {
    if (loaSignedAt && loaSignedByUser) {
      return true;
    }
    return false;
  }, [loaSignedAt, loaSignedByUser]);

  const isLOAGenerated = useMemo(() => {
    return !!loaUrl;
  }, [loaUrl]);

  useSubscribeToClinicSettingsGeneralTwilioVerificationSubscription({
    variables: { where: { node: { twilioCallSid: { equals: twilioCallSid } } } },
    skip: !twilioCallSid || currentVerificationStep === VerificationSteps.CallSuccessfullyVerified,
    fetchPolicy: GraphQLFetchPolicies.NetworkOnly,
    onData: ({ data }) => {
      if (data?.data?.twilioVerificationChanged?.node?.status === TwilioVerificationStatus.Success) {
        setCurrentVerificationStep(VerificationSteps.InformationCollection);
        resetState();
        refetchCurrentClinicData();
      }
    },
  });

  const getExtension = useCallback((): string => {
    return phoneTreeSelections
      .map((action: Action) => {
        if (action.type === PhoneTreeChoices.Wait) {
          return 'w'.repeat(action.value * 2);
        } else if (action.type === PhoneTreeChoices.Press) {
          return action.value;
        }
        return '';
      })
      .join('');
  }, [phoneTreeSelections]);

  const verifyPhoneNumber = useCallback(async (): Promise<void> => {
    const number = phoneNumber.replace(/-/g, '');

    setCurrentVerificationStep(VerificationSteps.CallVerifying);

    try {
      const { data, errors } = await twilioVerifyCallerId({
        variables: {
          data: {
            phoneNumber: number,
            friendlyName: currentClinic?.findUniqueClinic?.name || '',
            extension: getExtension(),
            callDelay: 0,
            isCallerId,
          },
        },
      });

      if (data?.twilioVerifyCallerId) {
        setCallerIdVerificationCode(data?.twilioVerifyCallerId.validationCode);
        setTwilioCallSid(data?.twilioVerifyCallerId.callSid);
      } else if (errors) {
        // Already Verified
        setCurrentVerificationStep(VerificationSteps.CallVerificationUnsuccessful);
        setVerificationError(errors[0].message);
      }
    } catch (e) {
      console.log(e);
      Sentry.captureException(e);
    }
  }, [phoneNumber, twilioVerifyCallerId, currentClinic?.findUniqueClinic?.name, getExtension, isCallerId]);

  const getLOAbanner = (): JSX.Element => {
    if (!isLOASigned) {
      return (
        <>
          <Button
            isDisabled={!isNumberVerified || !isClinicDataComplete}
            onClick={(): void => {
              setCurrentModalContent(ModalContentSteps.LOA);
            }}
          >
            Generate Letter of Authorization
          </Button>
          {(!isClinicDataComplete || !isClinicDataComplete) && (
            <Box marginTop={'12px'}>
              <Text variant="danger">
                We need your clinic’s full address, phone number and EIN before your LOA can be generated. Please go to{' '}
                <Text
                  as="span"
                  textDecoration="underline"
                  cursor="pointer"
                  variant="danger"
                  onClick={(): void => {
                    history.push('/settings/clinic/general');
                  }}
                >
                  General Info
                </Text>{' '}
                and fill out the missing fields.
              </Text>
            </Box>
          )}
        </>
      );
    }

    if (!isLOAGenerated) {
      return (
        <Flex
          marginBottom={'12px'}
          borderRadius={'4px'}
          backgroundColor="background.warning"
          padding={'8px 8px 8px 12px'}
          alignItems={'center'}
        >
          <Icon name="clock" color={'warning'} />
          <Box marginLeft={'17px'} fontWeight={'600'} borderRadius="4px">
            Generating Letter of authorization
          </Box>
        </Flex>
      );
    }

    return (
      <Flex
        marginBottom={'12px'}
        borderRadius={'4px'}
        backgroundColor="background.success"
        padding={'8px 8px 8px 12px'}
        alignItems={'center'}
      >
        <Icon name="checkmark" color={'success'} />
        <Box marginLeft={'17px'} fontWeight={'600'} borderRadius="4px">
          Letter of authorization generated
        </Box>
      </Flex>
    );
  };

  const renderModalContent = (): JSX.Element => {
    switch (currentModalContent) {
      case ModalContentSteps.Hosting:
        return (
          <ModalContent>
            {displayLastWarning ? (
              <CModalHeader fontSize={'30px'} marginBottom={'-10px'}>
                Are you sure?
              </CModalHeader>
            ) : (
              <CModalHeader>Enable Hosted Messaging</CModalHeader>
            )}
            <ModalCloseButton />
            {displayLastWarning ? (
              <CModalBody>
                This will start the process of removing SMS capabilities from your current phone provider.
                <Alert
                  status="error"
                  marginTop={'18px'}
                  height="100%"
                  width="100%"
                  iconProps={{ name: 'warningSign' }}
                  description="Only confirm when you are ready to use Otto as your sole SMS interface."
                  hideCloseButton
                />
              </CModalBody>
            ) : (
              <CModalBody>
                This process will take up to 72 hours. We will send an email to {userEmail} when Hosted Messaging is
                enabled.
                <div style={{ fontWeight: 600, margin: '30px 0' }}>
                  The phone number must be verified and letter of authorization generated to enable hosted messaging:
                </div>
                {!isNumberVerified ? (
                  <Button
                    onClick={(): void => {
                      setCurrentModalContent(ModalContentSteps.PhoneVerification);
                    }}
                    marginBottom={'12px'}
                  >
                    Verify Phone Number
                  </Button>
                ) : (
                  <Flex
                    borderRadius={'4px'}
                    marginBottom={'12px'}
                    backgroundColor="background.success"
                    padding={'8px 8px 8px 12px'}
                    alignItems={'center'}
                  >
                    <Icon name="checkmark" color={'success'} />
                    <Box marginLeft={'17px'} fontWeight={'600'} borderRadius="4px">
                      Phone number verified
                    </Box>
                  </Flex>
                )}
                {getLOAbanner()}
              </CModalBody>
            )}
            <CModalFooter
              marginBottom={'8px'}
              justifyContent={isLOASigned && isNumberVerified ? 'space-between' : 'flex-end'}
            >
              <Button
                onClick={(): void => {
                  resetState();
                  onClose();
                }}
                variant={'tertiary'}
              >
                {displayLastWarning ? 'Cancel' : 'Close'}
              </Button>
              {isLOASigned && isNumberVerified && (
                <Button
                  isLoading={!isLOAGenerated}
                  loadingText="Generating LOA"
                  onClick={(): void => {
                    if (displayLastWarning) {
                      handleStartPorting();
                    } else {
                      setDisplayLastWarning(true);
                    }
                  }}
                >
                  {displayLastWarning ? "Yes, I'm Sure" : 'Start Porting'}
                </Button>
              )}
            </CModalFooter>
          </ModalContent>
        );

      case ModalContentSteps.PhoneVerification:
        return (
          <ModalContent>
            <CModalHeader>Verify Phone Number</CModalHeader>
            <ModalCloseButton />
            <CModalBody>{renderVerificationSteps()}</CModalBody>
            <CModalFooter marginBottom={'8px'} justifyContent={'space-between'}>
              <Button
                onClick={(): void => {
                  resetState();
                }}
                variant={'tertiary'}
              >
                Back
              </Button>
              {renderVerificationButton()}
            </CModalFooter>
          </ModalContent>
        );
      case ModalContentSteps.LOA:
        return (
          <ModalContent>
            <CModalHeader>Letter of Authorization</CModalHeader>
            <ModalCloseButton />
            <CModalBody>
              {isImpersonating && (
                <Alert
                  maxWidth={'650px'}
                  status="warning"
                  marginBottom={'12px'}
                  title="Unable to submit letter of authorization"
                  description="Otto employees cannot submit letters of authorization. Please reach out to a clinic admin to
                    submit this form."
                  hideCloseButton
                />
              )}
              <Flex>
                <Box flex={1}>
                  <Box fontWeight={'600'}>Current Provider</Box>
                  <Box>{incomingCarrier}</Box>
                </Box>
                <Box flex={1}>
                  <Box>
                    <Box fontWeight={'600'}>Phone Number</Box>
                    <Box>{phoneNumber}</Box>
                  </Box>
                </Box>
              </Flex>
              <div style={{ fontWeight: 600, margin: '20px 0  10px 0' }}>Digital Signature</div>
              <TextInput marginBottom={'10px'} value={userName} />
              <Flex alignItems={'flex-start'}>
                <Checkbox
                  _focus={{ border: '0', boxShadow: 'none' }}
                  isInvalid={!!loaValidationError}
                  isChecked={isLOACheckedBox}
                  onChange={(): void => {
                    setIsLOACheckedBox((isLOACheckedBox) => !isLOACheckedBox);
                  }}
                >
                  By checking this box, I confirm that I own the phone number above and authorize Bandwidth to act on my
                  behalf to port the number.
                </Checkbox>
              </Flex>
              {!!loaValidationError && (
                <Box fontSize={'14px'} color="text.danger" marginTop={'4px'}>
                  {loaValidationError}
                </Box>
              )}
            </CModalBody>
            <CModalFooter marginBottom={'8px'} justifyContent={'space-between'}>
              <Button
                onClick={(): void => {
                  resetState();
                }}
                variant={'tertiary'}
              >
                Back
              </Button>
              <Button
                isDisabled={isImpersonating}
                onClick={(): void => {
                  signLettersOfAuthorization();
                }}
              >
                Submit
              </Button>
            </CModalFooter>
          </ModalContent>
        );
      default:
        break;
    }
    return <></>;
  };

  const renderVerificationSteps = (): JSX.Element => {
    switch (currentVerificationStep) {
      case VerificationSteps.InformationCollection:
        return (
          <VStack align="flex-start">
            <Text as="p">We will call {phoneNumber} and ask you to enter a 6-digit validation code.</Text>
            <Text fontWeight="bold" as="p">
              Have an extension or phone tree?
            </Text>
            {phoneTreeSelections.map((treeSelection, index) => {
              return (
                <HStack key={`tree-selection-${index}`} justifyContent="flex-start">
                  <Select
                    maxW="max-content"
                    isFieldInline
                    value={treeSelection.type}
                    listProps={{
                      onSelect: (selectedItem): void =>
                        changePhoneTreeSelectionTypeAtIndex(selectedItem?.value || '', index),
                    }}
                    options={[
                      { label: PhoneTreeChoices.Wait, value: PhoneTreeChoices.Wait },
                      { label: PhoneTreeChoices.Press, value: PhoneTreeChoices.Press },
                    ]}
                  />
                  <NumberInput
                    width="72px"
                    marginLeft="8px"
                    min={treeSelection.type === PhoneTreeChoices.Press ? 0 : 1}
                    max={9}
                    value={treeSelection.value}
                    onChange={(value): void => {
                      if (parseInt(value) >= 0) {
                        changePhoneTreeSelectionValueAtIndex(parseInt(value), index);
                      }
                    }}
                  >
                    <NumberInputField />
                    <NumberInputStepper>
                      <NumberIncrementStepper />
                      <NumberDecrementStepper />
                    </NumberInputStepper>
                  </NumberInput>
                  <Box marginLeft="8px" marginRight="12px">
                    {treeSelection.description}
                  </Box>
                  <Button
                    size="md"
                    variant="ghostNeutral"
                    iconName="trashcan"
                    cursor="pointer"
                    onClick={(): void => {
                      removePhoneTreeSelectionAtIndex(index);
                    }}
                  />
                </HStack>
              );
            })}
            <Button
              leftIcon={<Icon size="sm" name="plus" />}
              variant="secondary"
              size="sm"
              onClick={addNewPhoneTreeStep}
              marginBottom="12px"
            >
              Add Step
            </Button>
          </VStack>
        );

      case VerificationSteps.CallVerifying:
        return (
          <Flex flexDirection={'column'} alignItems={'center'}>
            <Box
              border="1px"
              borderColor="border.default"
              backgroundColor="background.subtle"
              padding={'12px 16px 12px 16px'}
              fontSize={'24px'}
              marginBottom={'28px'}
              borderRadius={'8px'}
            >
              {callerIdVerificationCode}
            </Box>
            <Box>Calling {phoneNumber}. Please answer the automated call and enter the 6-digit validation code.</Box>
          </Flex>
        );

      case VerificationSteps.CallVerificationUnsuccessful:
        return (
          <>
            <Alert status="error" mt="3" fontSize="sm" title="Unable to verify phone number" hideCloseButton />
            <Box mt={4}>{verificationError}</Box>
          </>
        );
      case VerificationSteps.CallSuccessfullyVerified:
        return <Box>Phone number successfully verified.</Box>;
    }
    return <></>;
  };

  const renderVerificationButton = (): JSX.Element => {
    switch (currentVerificationStep) {
      case VerificationSteps.InformationCollection:
        return <Button onClick={verifyPhoneNumber}>Ready for Call</Button>;
      case VerificationSteps.CallVerifying:
        return <Button isDisabled={true} isLoading loadingText="Calling" />;
    }
    return <></>;
  };

  const renderActionButtons = (): JSX.Element => {
    if (!isPrimary && status === PhoneNumberStatus.Hosted) {
      return (
        <Button size="sm" variant="secondary" onClick={handleActivatePhoneNumber}>
          Activate
        </Button>
      );
    }
    if (!isPrimary && status === PhoneNumberStatus.PortingInProgress) {
      return <Button size="sm" isDisabled={true} isLoading loadingText="Porting" />;
    }
    if (!isPrimary && status === PhoneNumberStatus.New && provider === ServiceProvider.Bandwidth) {
      return (
        <Button size="sm" onClick={handleEnablePhoneNumber}>
          Enable
        </Button>
      );
    }
    if (!isPrimary && status === PhoneNumberStatus.ReadyToHost && provider === ServiceProvider.Bandwidth) {
      return (
        <Button size="sm" onClick={handleEnablePhoneNumber}>
          Enable
        </Button>
      );
    }
    if (!isPrimary && status === PhoneNumberStatus.HostingNotAvailable && provider === ServiceProvider.Bandwidth) {
      return (
        <Button size="sm" onClick={handleStartPorting}>
          Retry Porting
        </Button>
      );
    }
    return <Box width={'81px'} />;
  };

  return (
    <div className="EnablePhoneNumber__" style={{ minWidth: '100px' }}>
      <CModal
        onClose={(): void => {
          resetState();
          onClose();
        }}
        isOpen={isOpen}
        isCentered
      >
        <ModalOverlay />
        {renderModalContent()}
      </CModal>
      {renderActionButtons()}
    </div>
  );
};

export default EnableBandwidthPhoneNumber;
